import React from 'react';
import { Box, Typography, FormControlLabel, Checkbox, Switch } from '@mui/material';
import type { FilterTrayStripProps, FilterTrayStripItem } from './types';

const FilterTrayStrip: React.FC<FilterTrayStripProps> = ({
  items,
  sticky = false,
  stickyTop = 500,
  sx = {},
}) => {
  return (
    <Box
      sx={{
        ...(sticky && {
          position: 'sticky',
          top: stickyTop,
          zIndex: 10,
          backgroundColor: 'background.paper',
          boxShadow: 1,
        }),
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
        px: 1.5,
        py: 1,
        mb: 3,
        flexWrap: 'wrap',
        ...sx,
      }}
    >
      {items.map((item: FilterTrayStripItem, index: number) => {
        if (item.type === 'switch') {
          return (
            <React.Fragment key={index}>
              {index > 0 && <Box component="span" sx={{ ml: 1 }} />}
              <Typography variant="body2" color="text.secondary" sx={{ mr: 0.25 }}>
                {item.label}
              </Typography>
              <FormControlLabel
                control={
                  <Switch
                    size="small"
                    checked={item.value}
                    onChange={(_, checked) => item.onChange(checked)}
                    color="primary"
                  />
                }
                label={
                  item.switchLabel ? (
                    <Typography variant="body2">{item.switchLabel}</Typography>
                  ) : undefined
                }
              />
            </React.Fragment>
          );
        }
        if (item.type === 'checkboxPair') {
          const atLeastOne = item.atLeastOne !== false;
          const checkedCount = item.options.filter(o => o.checked).length;
          return (
            <React.Fragment key={index}>
              {index > 0 && <Box component="span" sx={{ ml: 1 }} />}
              <Typography variant="body2" color="text.secondary" sx={{ mr: 0.25 }}>
                {item.groupLabel}
              </Typography>
              {item.options.map(opt => {
                const isDisabled =
                  atLeastOne &&
                  opt.checked &&
                  checkedCount <= 1 &&
                  item.options.every(o => !o.checked || o.id === opt.id);
                return (
                  <FormControlLabel
                    key={opt.id}
                    control={
                      <Checkbox
                        size="small"
                        checked={opt.checked}
                        disabled={isDisabled}
                        onChange={(_, checked) => {
                          if (atLeastOne && opt.checked && checkedCount <= 1 && !checked) {
                            return;
                          }
                          item.onChange(opt.id, checked);
                        }}
                      />
                    }
                    label={<Typography variant="body2">{opt.label}</Typography>}
                  />
                );
              })}
            </React.Fragment>
          );
        }
        return null;
      })}
    </Box>
  );
};

export default FilterTrayStrip;
export type { FilterTrayStripProps, FilterTrayStripItem, FilterTraySwitchItem, FilterTrayCheckboxPairItem } from './types';
